<script setup lang="ts">
// The environment's mails (docs/proposal-mail-tab.md): GET /environments/{name}/mails. Every mail the environment
// sends goes to mail:25, where its mailpit keeps it and delivers nothing. The API decides who may read; this offers
// mailpit's search, paging, one mail as HTML (sandboxed, remote images off until asked), text or headers, the .eml
// download, and for operators deleting them all.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { must } from '../api/client';
import type { Mail, MailList, MailSummary } from '../api/types';
import { useContext } from '../context';
import ProblemAlert from './ProblemAlert.vue';
import Icon from './Icon.vue';
import { addressList, addressText, frameDoc, hasRemote, mailTime, senderShort, sizeText } from '../mail/mail';

const props = defineProps<{ environment: string; raw?: boolean; source?: string; canDelete?: boolean }>();
const { api } = useContext();

const PAGE = 50;
const q = ref('');
const start = ref(0);
const list = ref<MailList>();
const loading = ref(false);
const error = ref<unknown>();

const selected = ref<string>();
const mail = ref<Mail>();
const mailLoading = ref(false);
const mailError = ref<unknown>();
const view = ref<'html' | 'text' | 'headers'>('html');
const remote = ref(false);
const confirmDelete = ref(false);
const deleted = ref(false);

let seq = 0;
let mailSeq = 0;
let searchTimer: number | undefined;

async function load() {
  const mine = ++seq;
  loading.value = true;
  try {
    const r = must(await api.GET('/environments/{name}/mails', {
      params: { path: { name: props.environment }, query: { q: q.value.trim() || undefined, start: start.value, limit: PAGE } },
    }));
    if (mine !== seq) return;
    list.value = r;
    error.value = undefined;
  } catch (e) {
    if (mine === seq) error.value = e;
  } finally {
    if (mine === seq) loading.value = false;
  }
}

async function open(m: MailSummary) {
  const mine = ++mailSeq;
  selected.value = m.id;
  mailLoading.value = true;
  remote.value = false;
  try {
    const r = must(await api.GET('/environments/{name}/mails/{id}', { params: { path: { name: props.environment, id: m.id } } }));
    if (mine !== mailSeq) return;
    mail.value = r;
    mailError.value = undefined;
    m.read = true;
    if (!r.html && view.value === 'html') view.value = 'text';
    else if (r.html && view.value === 'text') view.value = 'html';
  } catch (e) {
    if (mine === mailSeq) {
      mail.value = undefined;
      mailError.value = e;
    }
  } finally {
    if (mine === mailSeq) mailLoading.value = false;
  }
}

async function downloadSource() {
  const m = mail.value;
  if (!m) return;
  try {
    const r = await api.GET('/environments/{name}/mails/{id}/source', {
      params: { path: { name: props.environment, id: m.id } }, parseAs: 'blob',
    });
    const blob = r.data as Blob | undefined;
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${props.environment}-${m.id}.eml`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    mailError.value = e;
  }
}

async function deleteAll() {
  confirmDelete.value = false;
  try {
    await api.DELETE('/environments/{name}/mails', { params: { path: { name: props.environment } } });
    selected.value = undefined;
    mail.value = undefined;
    start.value = 0;
    deleted.value = true;
    await load();
  } catch (e) {
    error.value = e;
  }
}

function page(delta: number) {
  start.value = Math.max(0, start.value + delta * PAGE);
  void load();
}

watch(q, () => {
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => {
    start.value = 0;
    void load();
  }, 400);
});

onMounted(load);
onBeforeUnmount(() => window.clearTimeout(searchTimer));

const total = computed(() => list.value?.total ?? 0);
const rangeText = computed(() => {
  const l = list.value;
  if (!l || !l.messages.length) return '';
  return `${(l.start + 1).toLocaleString('en')}–${(l.start + l.messages.length).toLocaleString('en')} of ${l.total.toLocaleString('en')}`;
});
const srcdoc = computed(() => (mail.value?.html ? frameDoc(mail.value.html, remote.value) : ''));
const remoteContent = computed(() => !!mail.value?.html && hasRemote(mail.value.html));
</script>

<template>
  <div class="mail">
    <p v-if="raw" class="alert banner" role="note">
      <Icon name="lock" :size="18" />
      <span class="grow"><strong>Raw production data.</strong> These mails may hold customer data: you see them because you are on the access list
        and entitled to <code>{{ source }}</code>. Every listing and every mail opened is audited.</span>
    </p>

    <section class="card mail-bar" aria-label="Search">
      <label class="mail-search">
        <span class="sr-only">Search the mails</span>
        <Icon name="search" :size="16" />
        <input v-model="q" type="search" placeholder="Search, e.g. to:jane@example.com, subject:Bestellung, has:attachment, or words" maxlength="200" data-testid="mail-q" />
      </label>
      <button type="button" class="button sm" :disabled="loading" data-testid="mail-refresh" @click="load">Refresh</button>
      <template v-if="canDelete">
        <button v-if="!confirmDelete" type="button" class="button sm danger" :disabled="!total" data-testid="mail-delete" @click="confirmDelete = true">Delete all…</button>
        <span v-else class="inline mail-confirm" role="group" aria-label="Confirm deleting">
          <span>Delete every mail of {{ environment }}?</span>
          <button type="button" class="button sm danger-solid" data-testid="mail-delete-confirm" @click="deleteAll">Delete all</button>
          <button type="button" class="button sm" @click="confirmDelete = false">Cancel</button>
        </span>
      </template>
    </section>

    <ProblemAlert :error="error" context="Mail" />
    <p v-if="deleted && !total" class="info" role="status">Every mail was deleted.</p>

    <div class="mail-cols">
      <section class="card mail-list" aria-labelledby="mail-h">
        <div class="mail-head">
          <h2 id="mail-h">{{ total.toLocaleString('en') }} mails</h2>
          <span v-if="loading" class="muted" aria-live="polite">loading…</span>
          <span class="muted end">newest first</span>
        </div>
        <ul class="mail-items" data-testid="mail-list">
          <li v-for="m in list?.messages ?? []" :key="m.id">
            <button type="button" class="mail-item" :class="{ unread: !m.read, on: m.id === selected }" :aria-current="m.id === selected ? 'true' : undefined"
              :data-testid="`mail-${m.id}`" @click="open(m)">
              <span class="row1"><span class="from">{{ senderShort(m.from) }}</span><span class="t" :title="m.created ?? ''">{{ mailTime(m.created) }}</span></span>
              <span class="subject">{{ m.subject || '(no subject)' }}<span v-if="m.attachments" class="att" :title="`${m.attachments} attachment(s)`"> · 📎 {{ m.attachments }}</span></span>
              <span class="to muted">to {{ addressList(m.to) }}</span>
              <span class="snippet muted">{{ m.snippet }}</span>
            </button>
          </li>
        </ul>
        <p v-if="list && !list.messages.length && !loading" class="empty">
          <template v-if="q.trim()">No mail matches the search.</template>
          <template v-else>No mail yet. Everything the environment sends to <code>mail:25</code> shows up here; nothing is delivered.</template>
        </p>
        <div class="mail-foot">
          <span class="muted">{{ rangeText }}</span>
          <span class="end inline">
            <button type="button" class="button sm" :disabled="loading || start === 0" data-testid="mail-newer" @click="page(-1)">Newer</button>
            <button type="button" class="button sm" :disabled="loading || !list || list.start + list.messages.length >= list.total" data-testid="mail-older" @click="page(1)">Older</button>
          </span>
        </div>
      </section>

      <section class="card mail-view" aria-label="The mail">
        <ProblemAlert :error="mailError" context="Mail" />
        <p v-if="!selected" class="empty">Pick a mail on the left.</p>
        <p v-else-if="mailLoading && !mail" class="muted">loading…</p>
        <template v-else-if="mail">
          <h2 class="mail-subject" data-testid="mail-subject">{{ mail.subject || '(no subject)' }}</h2>
          <dl class="mail-meta">
            <dt>From</dt><dd>{{ addressText(mail.from) }}</dd>
            <dt>To</dt><dd>{{ addressList(mail.to) }}</dd>
            <template v-if="mail.cc.length"><dt>Cc</dt><dd>{{ addressList(mail.cc) }}</dd></template>
            <template v-if="mail.bcc.length"><dt>Bcc</dt><dd>{{ addressList(mail.bcc) }}</dd></template>
            <template v-if="mail.replyTo.length"><dt>Reply-To</dt><dd>{{ addressList(mail.replyTo) }}</dd></template>
            <dt>Date</dt><dd :title="mail.date ?? ''">{{ mailTime(mail.date) }} · {{ sizeText(mail.size) }}</dd>
          </dl>
          <div class="mail-tools">
            <div class="seg" role="radiogroup" aria-label="Show">
              <label><input v-model="view" type="radio" name="mail-view" value="html" :disabled="!mail.html" data-testid="mail-view-html" />HTML</label>
              <label><input v-model="view" type="radio" name="mail-view" value="text" data-testid="mail-view-text" />Text</label>
              <label><input v-model="view" type="radio" name="mail-view" value="headers" />Details</label>
            </div>
            <label v-if="view === 'html' && remoteContent" class="inline remote"><input v-model="remote" type="checkbox" data-testid="mail-remote" /> Load remote images</label>
            <button type="button" class="button sm end" data-testid="mail-source" @click="downloadSource">Download .eml</button>
          </div>
          <iframe v-if="view === 'html' && mail.html" class="mail-frame" sandbox="allow-popups allow-popups-to-escape-sandbox"
            referrerpolicy="no-referrer" :srcdoc="srcdoc" title="The mail's HTML" data-testid="mail-frame"></iframe>
          <pre v-else-if="view === 'text'" class="mail-text" data-testid="mail-text">{{ mail.text || '(this mail has no text part)' }}</pre>
          <dl v-else-if="view === 'headers'" class="mail-meta">
            <dt>Message-ID</dt><dd class="mono">{{ mail.messageId || '—' }}</dd>
            <dt>mailpit id</dt><dd class="mono">{{ mail.id }}</dd>
          </dl>
          <div v-if="mail.attachments.length" class="mail-atts">
            <strong>Attachments</strong>
            <ul>
              <li v-for="a in mail.attachments" :key="a.partId">{{ a.fileName || '(unnamed)' }} <span class="muted">{{ a.contentType }} · {{ sizeText(a.size) }}</span></li>
            </ul>
            <p class="hint">They are in the .eml download.</p>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<style scoped>
.mail { display: flex; flex-direction: column; gap: 1.25rem; }
.mail-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem 0.75rem; padding: 0.85rem 1rem; }
.mail-search { position: relative; flex: 1 1 320px; min-width: 0; display: flex; align-items: center; }
.mail-search :deep(svg) { position: absolute; left: 11px; color: var(--muted); pointer-events: none; }
.mail-search input { width: 100%; padding-left: 34px; }
.mail-confirm { gap: 0.5rem; }
.mail-cols { display: grid; grid-template-columns: minmax(300px, 420px) minmax(0, 1fr); gap: 1.25rem; align-items: start; }
@media (max-width: 900px) { .mail-cols { grid-template-columns: 1fr; } }
.mail-list { padding: 0; overflow: hidden; }
.mail-head, .mail-foot { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem 1rem; }
.mail-head h2 { margin: 0; font-size: 15px; }
.mail-foot { border-top: 1px solid var(--line); }
.end { margin-left: auto; }
.inline { display: inline-flex; align-items: center; gap: 0.4rem; }
.mail-items { list-style: none; margin: 0; padding: 0; max-height: 70vh; overflow-y: auto; border-top: 1px solid var(--line); }
.mail-items li + li { border-top: 1px solid var(--line); }
.mail-item { display: flex; flex-direction: column; gap: 2px; width: 100%; padding: 0.6rem 1rem; text-align: left; background: none; border: 0; border-radius: 0; cursor: pointer; font: inherit; color: inherit; }
.mail-item:hover { background: var(--hover, rgba(0, 0, 0, 0.03)); }
.mail-item.on { background: var(--selected, rgba(37, 99, 235, 0.08)); box-shadow: inset 3px 0 0 var(--accent, #2563eb); }
.mail-item.unread .from, .mail-item.unread .subject { font-weight: 600; }
.row1 { display: flex; gap: 0.5rem; }
.from { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.t { font-size: 12px; color: var(--muted); white-space: nowrap; }
.subject, .to, .snippet { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.to, .snippet { font-size: 12.5px; }
.att { font-weight: 400; color: var(--muted); }
.empty { padding: 1rem; color: var(--muted); }
.mail-view { display: flex; flex-direction: column; gap: 0.75rem; min-width: 0; }
.mail-subject { margin: 0; font-size: 18px; overflow-wrap: anywhere; }
.mail-meta { display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 4px 12px; margin: 0; font-size: 13px; }
.mail-meta dt { color: var(--muted); }
.mail-meta dd { margin: 0; overflow-wrap: anywhere; }
.mail-tools { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem 0.75rem; }
.remote { font-size: 13px; }
.mail-frame { width: 100%; height: 70vh; border: 1px solid var(--line); border-radius: 8px; background: #fff; }
.mail-text { margin: 0; padding: 1rem; max-height: 70vh; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; border: 1px solid var(--line); border-radius: 8px; font-size: 13px; }
.mail-atts ul { margin: 0.25rem 0; padding-left: 1.2rem; font-size: 13px; }
</style>
